import { useEffect, useRef } from "react";
import type { PointerEvent } from "react";
import type { PayslipSignatureInput } from "@cyh/contracts";
import { useLanguage } from "../i18n";

export function PayslipSignaturePad({ value, onChange, disabled }: { value?: PayslipSignatureInput; onChange: (value?: PayslipSignatureInput) => void; disabled: boolean }) {
  const { t } = useLanguage();
  const canvas = useRef<HTMLCanvasElement>(null), drawing = useRef<number | null>(null);
  const strokes = useRef<PayslipSignatureInput["strokes"]>([]);
  useEffect(() => {
    strokes.current = value?.strokes.map(stroke => stroke.map(point => ({ ...point }))) ?? [];
    const ctx = canvas.current?.getContext("2d"); if (!ctx) return;
    ctx.clearRect(0, 0, 600, 200); ctx.strokeStyle = "#172d26"; ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.lineJoin = "round";
    for (const stroke of strokes.current) { ctx.beginPath(); stroke.forEach((p, i) => i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)); ctx.stroke(); }
  }, [value]);
  const position = (event: PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: Math.round(Math.max(0, Math.min(600, (event.clientX - rect.left) * 600 / rect.width)) * 10) / 10,
      y: Math.round(Math.max(0, Math.min(200, (event.clientY - rect.top) * 200 / rect.height)) * 10) / 10 };
  };
  function finish(event: PointerEvent<HTMLCanvasElement>) {
    if (drawing.current !== event.pointerId) return;
    drawing.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    const next = strokes.current.filter(stroke => stroke.length >= 2).map(stroke => [...stroke]);
    onChange(next.length ? { strokes: next } : undefined);
  }
  return <div className="payslip-signature-pad"><p>{t("请用电脑鼠标或手机手指，清楚写出与当前账号一致的完整姓名。系统会识别并比对；识别不出或不一致不能确认，可清除后重签。签名不代表工资到账。", "Clearly write your full account name with a mouse or finger. Unreadable or mismatched signatures cannot confirm the payslip; clear and retry. Signing does not acknowledge payment receipt.")}</p>
    <canvas ref={canvas} width={600} height={200} aria-label={t("手写签名区域", "Handwritten signature area")} style={{ display: "block", width: "100%", maxWidth: 600, aspectRatio: "3 / 1", touchAction: "none", border: "1px solid #a3b8ad", borderRadius: 8, background: "white", cursor: disabled ? "default" : "crosshair" }}
      onPointerDown={event => {
        if (disabled || drawing.current !== null || (event.pointerType === "mouse" && event.button !== 0) || strokes.current.length >= 64) return;
        event.preventDefault(); drawing.current = event.pointerId; event.currentTarget.setPointerCapture(event.pointerId);
        strokes.current.push([position(event)]);
      }}
      onPointerMove={event => {
        if (disabled || drawing.current !== event.pointerId) return;
        event.preventDefault(); const stroke = strokes.current.at(-1)!;
        if (stroke.length >= 2000 || strokes.current.reduce((n, s) => n + s.length, 0) >= 3000) return;
        const point = position(event), previous = stroke.at(-1)!; stroke.push(point);
        const ctx = canvas.current?.getContext("2d"); if (!ctx) return;
        ctx.beginPath(); ctx.moveTo(previous.x, previous.y); ctx.lineTo(point.x, point.y); ctx.stroke();
      }} onPointerUp={finish} onPointerCancel={finish} onLostPointerCapture={finish} />
    <button type="button" disabled={disabled} onClick={() => onChange(undefined)}>{t("清除重签", "Clear and sign again")}</button>
  </div>;
}
